import React, { useEffect, useState } from "react";
import { Button, CircularProgress, Dialog, InputBase } from "@mui/material";
import { ErrorOutline } from "@mui/icons-material";
import { X } from "lucide-react";
import { impersonate } from "../../api/adminApi";
import { startImpersonation } from "../../utils/impersonation";

const ImpersonateModal = ({
  open,
  setOpen,
  orgId,
  ownerEmail,
  ownerUserId,
  organizationName,
}) => {
  const [reason, setReason] = useState("");
  const [loader, setLoader] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setReason("");
    setError(null);
    setLoader(false);
  }, [open]);

  const targetLabel = ownerEmail || ownerUserId || "owner";

  const submit = async () => {
    setError(null);
    if (!reason.trim()) {
      setError("Reason is required");
      return;
    }
    if (!ownerUserId && !ownerEmail) {
      setError("No owner found for this org");
      return;
    }
    setLoader(true);
    try {
      // Prefer userId — detail payload often lacks ownerEmail.
      const data = await impersonate({
        ...(ownerUserId ? { userId: ownerUserId } : { email: ownerEmail }),
        reason: reason.trim(),
      });
      const accessToken = data.accessToken || data.access_token;
      if (!accessToken) {
        throw new Error("Impersonation response missing access token");
      }
      await startImpersonation({
        accessToken,
        expiresIn: data.expiresIn || data.expires_in,
        user: data.user,
        orgId,
        label: organizationName || ownerEmail || ownerUserId,
        returnPath: `/admin/orgs/${orgId}`,
      });
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.msg ||
          err?.message ||
          "Impersonation failed"
      );
      setLoader(false);
    }
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      BackdropProps={{ style: { backgroundColor: "rgb(144,150,160,0.8)" } }}
    >
      <div className="px-8 py-6">
        <div className="relative mb-3">
          <div className="lg:text-[20px] text-[18px] font-semibold">
            Impersonate org
          </div>
          <div className="absolute top-0 right-0">
            <X
              className="w-[20px] text-gray-400 cursor-pointer"
              onClick={() => !loader && setOpen(false)}
            />
          </div>
        </div>
        <p className="text-[13px] text-gray-500 mb-3">
          Log in as{" "}
          <span className="font-semibold text-gray-800">{targetLabel}</span>
          {organizationName ? ` (${organizationName})` : ""}. Session lasts
          ~15 minutes with no refresh token.
        </p>
        {error && (
          <div className="text-red-500 text-[13px] font-semibold mb-2">
            <ErrorOutline sx={{ fontSize: 16 }} /> {error}
          </div>
        )}
        <InputBase
          placeholder="Reason (required, logged to activity)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          multiline
          minRows={2}
          sx={{
            bgcolor: "white",
            border: "1px solid #E0E0E0",
            width: "100%",
            px: 2,
            py: 1,
            fontSize: "14px",
            borderRadius: "8px",
          }}
        />
        <div className="flex justify-end gap-2 mt-5">
          <Button
            onClick={() => setOpen(false)}
            disabled={loader}
            sx={{ textTransform: "none", color: "#6B7280" }}
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={loader}
            sx={{
              textTransform: "none",
              bgcolor: "#605BFF",
              color: "white",
              borderRadius: "8px",
              px: 2.5,
              "&:hover": { bgcolor: "#4F46E5" },
            }}
          >
            {loader ? (
              <CircularProgress size={18} sx={{ color: "white" }} />
            ) : (
              "Start support session"
            )}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default ImpersonateModal;
